import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { EvidenceBacklinkDTO, ProvenanceChain, ProvenanceNode } from '@deepguide-ai/types'
import { ExternalLink, Loader2, TriangleAlert } from 'lucide-react'
import { SubmissionDetailsCard } from '@/components/compliance/SubmissionDetailsCard'
import { ArtifactsPanel } from '@/components/evidence-viewer/ArtifactsPanel'
import MainPreviewArea from '@/components/evidence-viewer/MainPreviewArea'
import { Badge } from '@deepguide-ai/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@deepguide-ai/ui/card'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { AssertionSourceBadge } from './AssertionSourceBadge'

const TYPE_LABELS: Record<ProvenanceNode['type'], string> = {
  policy: 'Policy',
  claim: 'Claim',
  test: 'Test',
  submission: 'Submission',
  pack: 'Evidence Pack',
  backlink: 'Backlink',
  control: 'Control',
}

function isString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function getMetadataString(node: ProvenanceNode, key: string) {
  const value = node.metadata?.[key]
  return isString(value) ? value : null
}

function getMetadataNumber(node: ProvenanceNode, key: string) {
  const value = node.metadata?.[key]
  return isNumber(value) ? value : null
}

function getMetadataStringArray(node: ProvenanceNode, key: string) {
  const value = node.metadata?.[key]
  if (!Array.isArray(value)) return []
  return value.filter(isString)
}

function formatTimestamp(value?: string | null) {
  if (!value) return null

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleString()
}

function DetailRow({
  label,
  value,
}: {
  label: string
  value?: string | null
}) {
  if (!value) return null

  return (
    <div className='space-y-1.5'>
      <div className='text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground/90'>
        {label}
      </div>
      <div className='break-words text-[15px] leading-6 text-foreground'>{value}</div>
    </div>
  )
}

function DetailSectionRow({
  label,
  value,
}: {
  label: string
  value?: string | null
}) {
  if (!value) return null

  return (
    <div className='space-y-1.5'>
      <div className='text-sm font-semibold leading-6 text-foreground'>{label}</div>
      <div className='break-words text-[15px] leading-7 text-foreground/90'>{value}</div>
    </div>
  )
}

function DetailLinkRow({
  label,
  text,
  to,
  params,
}: {
  label: string
  text: string
  to:
    | '/workspaces/$workspaceId/tests/$testId'
    | '/workspaces/$workspaceId/evidence-viewer/$evidencePackId'
    | '/workspaces/$workspaceId/controls/$controlDefId'
    | '/workspaces/$workspaceId/submissions/$submissionId'
  params:
    | { workspaceId: string; testId: string }
    | { workspaceId: string; evidencePackId: string }
    | { workspaceId: string; controlDefId: string }
    | { workspaceId: string; submissionId: string }
}) {
  return (
    <div className='space-y-1.5'>
      <div className='text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground/90'>
        {label}
      </div>
      <Link
        to={to}
        params={params}
        className='inline-flex items-center gap-1.5 text-[15px] leading-6 text-primary underline-offset-4 hover:underline'
      >
        <span>{text}</span>
        <ExternalLink className='h-3.5 w-3.5 shrink-0' />
      </Link>
    </div>
  )
}

function isTextResponse(backlink: EvidenceBacklinkDTO) {
  if (!backlink.location) return false
  if ('artifactType' in backlink.location) {
    return backlink.location.artifactType === 'text_response'
  }
  return false
}

function getBacklinkLocationLabel(backlink: EvidenceBacklinkDTO) {
  if (backlink.screenshotId) {
    return 'Screenshot'
  }

  if (backlink.evidenceFileId) {
    const location = backlink.location
    if (location) {
      if ('page' in location && location.page) {
        return `Page ${location.page}${location.paragraph ? `, Para ${location.paragraph}` : ''}`
      }
      if ('cellRange' in location && location.cellRange) {
        return `Cells ${location.cellRange}`
      }
      if ('lineStart' in location && location.lineStart) {
        return `Lines ${location.lineStart}-${location.lineEnd || location.lineStart}`
      }
      if ('boundingBox' in location && location.boundingBox) {
        return 'Image region'
      }
    }
    return 'Document'
  }

  if (isTextResponse(backlink)) {
    return 'Text Response'
  }

  return 'Unknown'
}

function getBacklinkEvidencePackId(node: ProvenanceNode, chain?: ProvenanceChain | null) {
  if (node.type !== 'backlink' || !chain) return null

  const backlinkEdge = chain.edges.find((edge) => edge.to === node.id && edge.relationship === 'references')
  if (!backlinkEdge) return null

  const packNode = chain.nodes.find((chainNode) => chainNode.id === backlinkEdge.from && chainNode.type === 'pack')
  if (!packNode) return null

  return getMetadataString(packNode, 'evidencePackId') ?? packNode.id
}

function getDetailRows(node: ProvenanceNode) {
  switch (node.type) {
    case 'policy':
      return [
        { label: 'Policy ID', value: getMetadataString(node, 'policyDocumentId') ?? node.id },
        { label: 'Policy Slug', value: getMetadataString(node, 'policySlug') },
        { label: 'Program ID', value: getMetadataString(node, 'programId') },
      ]
    case 'claim':
      return [
        { label: 'Claim ID', value: getMetadataString(node, 'claimId') ?? node.id },
        { label: 'Policy', value: getMetadataString(node, 'policyName') },
        { label: 'Control Ref', value: getMetadataString(node, 'controlRef') },
      ]
    case 'test': {
      const controlRefs = getMetadataStringArray(node, 'controlRefs')
      return [
        { label: 'Task Code', value: getMetadataString(node, 'taskCode') },
        { label: 'Control Refs', value: controlRefs.length > 0 ? controlRefs.join(', ') : null },
      ]
    }
    case 'submission':
      return [
        { label: 'Submission ID', value: getMetadataString(node, 'submissionId') ?? node.id },
        { label: 'Status ID', value: getMetadataString(node, 'statusId') },
        { label: 'Source Execution', value: getMetadataString(node, 'sourceExecutionId') },
        {
          label: 'Overall Score',
          value: getMetadataNumber(node, 'overallScore') != null
            ? String(getMetadataNumber(node, 'overallScore'))
            : null,
        },
      ]
    case 'pack':
      return [
        { label: 'Pack ID', value: getMetadataString(node, 'evidencePackId') ?? node.id },
        { label: 'Submission ID', value: getMetadataString(node, 'submissionId') },
        { label: 'Pack Status', value: getMetadataString(node, 'status') },
      ]
    case 'backlink': {
      const confidence = getMetadataNumber(node, 'confidence')
      return [
        { label: 'Backlink ID', value: getMetadataString(node, 'backlinkId') ?? node.id },
        { label: 'Requirement ID', value: getMetadataString(node, 'requirementId') },
        { label: 'Evidence File ID', value: getMetadataString(node, 'evidenceFileId') },
        { label: 'Screenshot ID', value: getMetadataString(node, 'screenshotId') },
        { label: 'Confidence', value: confidence != null ? String(confidence) : null },
      ]
    }
    case 'control':
      return [
        { label: 'Control ID', value: getMetadataString(node, 'controlDefId') ?? node.id },
        { label: 'Control Ref', value: getMetadataString(node, 'controlRef') ?? node.label },
        { label: 'Program ID', value: getMetadataString(node, 'programId') },
        { label: 'Framework ID', value: getMetadataString(node, 'frameworkId') },
      ]
    default:
      return []
  }
}

function getNavigationTarget(
  node: ProvenanceNode,
  workspaceId: string,
  chain?: ProvenanceChain | null
) {
  if (node.type === 'test') {
    const testId = getMetadataString(node, 'testId') ?? node.id
    return {
      to: '/workspaces/$workspaceId/tests/$testId' as const,
      params: { workspaceId, testId },
      label: 'Open test',
    }
  }

  if (node.type === 'pack') {
    const evidencePackId = getMetadataString(node, 'evidencePackId') ?? node.id
    return {
      to: '/workspaces/$workspaceId/evidence-viewer/$evidencePackId' as const,
      params: { workspaceId, evidencePackId },
      label: 'Open evidence pack',
    }
  }

  if (node.type === 'submission') {
    const submissionId = getMetadataString(node, 'submissionId') ?? node.id
    return {
      to: '/workspaces/$workspaceId/submissions/$submissionId' as const,
      params: { workspaceId, submissionId },
      label: 'Open submission',
    }
  }

  if (node.type === 'backlink') {
    const evidencePackId = getBacklinkEvidencePackId(node, chain)
    if (!evidencePackId) return null

    return {
      to: '/workspaces/$workspaceId/evidence-viewer/$evidencePackId' as const,
      params: { workspaceId, evidencePackId },
      label: 'Open evidence pack',
    }
  }

  if (node.type === 'control') {
    const controlDefId = getMetadataString(node, 'controlDefId') ?? node.id
    return {
      to: '/workspaces/$workspaceId/controls/$controlDefId' as const,
      params: { workspaceId, controlDefId },
      label: 'Open control',
    }
  }

  return null
}

export function LineageDetailsPanel({
  node,
  chain,
  workspaceId,
  className,
  readOnly = false,
}: {
  node: ProvenanceNode | null
  chain?: ProvenanceChain | null
  workspaceId: string
  className?: string
  readOnly?: boolean
}) {
  const submissionId =
    node?.type === 'pack' || node?.type === 'submission'
      ? getMetadataString(node, 'submissionId') ?? node.id
      : null

  const submissionQuery = useQuery({
    ...trpc.guidedTemplate.getSubmission.queryOptions({
      id: submissionId ?? '__lineage-submission-disabled__',
    }),
    enabled: !!submissionId,
  })

  const [selectedSubmissionFile, setSelectedSubmissionFile] = useState<{
    type: 'screenshot' | 'attachment'
    id: string
  } | null>(null)

  const backlinkId = node?.type === 'backlink'
    ? getMetadataString(node, 'backlinkId') ?? node.id
    : null

  const backlinkEvidencePackId = node?.type === 'backlink'
    ? getBacklinkEvidencePackId(node, chain)
    : null

  const backlinkListQuery = useQuery({
    ...trpc.evidencePack.getBacklinks.queryOptions({
      evidencePackId: backlinkEvidencePackId ?? '__lineage-backlink-pack-disabled__',
    }),
    enabled: node?.type === 'backlink' && !!backlinkEvidencePackId,
  })

  const backlinkPackQuery = useQuery({
    ...trpc.evidencePack.getForViewer.queryOptions({
      evidencePackId: backlinkEvidencePackId ?? '__lineage-backlink-pack-disabled__',
    }),
    enabled: node?.type === 'backlink' && !!backlinkEvidencePackId,
  })

  const submissionEvidencePackId = node?.type === 'submission'
    ? submissionQuery.data?.submission?.evidencePack?.id ?? null
    : null

  const submissionPackQuery = useQuery({
    ...trpc.evidencePack.getForViewer.queryOptions({
      evidencePackId: submissionEvidencePackId ?? '__lineage-submission-pack-disabled__',
    }),
    enabled: node?.type === 'submission' && !!submissionEvidencePackId,
  })

  useEffect(() => {
    setSelectedSubmissionFile(null)
  }, [node?.id])

  if (!node) {
    return (
      <Card className={cn('flex h-full min-h-0 flex-col shadow-none', className)}>
        <CardContent className='flex flex-1 items-center justify-center text-sm text-muted-foreground'>
          Select a node to view details.
        </CardContent>
      </Card>
    )
  }

  const detailRows = getDetailRows(node)
  const formattedTimestamp = formatTimestamp(node.timestamp)
  const navigationTarget = readOnly
    ? null
    : getNavigationTarget(node, workspaceId, chain)
  const backlink = node.type === 'backlink'
    ? backlinkListQuery.data?.find((item) => item.id === backlinkId)
    : null
  const backlinkRequirementId = backlink?.requirementId ?? getMetadataString(node, 'requirementId')
  const backlinkRequirement = node.type === 'backlink'
    ? backlinkPackQuery.data?.requirements.find((requirement) => requirement.id === backlinkRequirementId)
    : null
  const backlinkReasoning = node.type === 'backlink'
    ? backlinkPackQuery.data?.internal?.elementScores?.find((score) => score.elementId === backlinkRequirementId)?.reasoningText
    : null

  return (
    <Card className={cn('flex h-full min-h-0 flex-col shadow-none !gap-0', className)}>
      <CardHeader className='space-y-4 border-b pb-4'>
        <div className='space-y-2'>
          <div className='flex flex-wrap items-center gap-2'>
            <Badge variant='outline' className='rounded-full'>
              {TYPE_LABELS[node.type]}
            </Badge>
            {node.status ? (
              <Badge variant='secondary' className='rounded-full'>
                {node.status}
              </Badge>
            ) : null}
            <AssertionSourceBadge source={node.assertionSource} />
          </div>
          <CardTitle className='text-base leading-6'>{node.label}</CardTitle>
        </div>

        <div className='grid gap-3 sm:grid-cols-2'>
          <DetailRow label='Timestamp' value={formattedTimestamp} />
          {navigationTarget ? (
            <DetailLinkRow
              label='Open'
              text={navigationTarget.label}
              to={navigationTarget.to}
              params={navigationTarget.params}
            />
          ) : null}
        </div>
      </CardHeader>

      <ScrollArea className='min-h-0 flex-1 p-6' type='always'>
        <div className='space-y-6'>
          {node.type !== 'pack' && node.type !== 'submission' && node.type !== 'backlink' ? (
            <div className='space-y-5'>
              <div className='space-y-5'>
                {detailRows.map((row) => (
                  <DetailSectionRow key={row.label} label={row.label} value={row.value} />
                ))}
              </div>
            </div>
          ) : null}

          {node.type === 'backlink' ? (
            <div className='space-y-5'>
              {backlinkEvidencePackId ? (
                backlinkListQuery.isLoading || backlinkPackQuery.isLoading ? (
                  <div className='flex items-center gap-2 rounded-md border px-4 py-3 text-sm text-muted-foreground'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    Loading backlink details…
                  </div>
                ) : backlinkListQuery.error || backlinkPackQuery.error ? (
                  <div className='flex items-center gap-2 rounded-md border border-red-200 px-4 py-3 text-sm text-status-red'>
                    <TriangleAlert className='h-4 w-4 text-destructive' />
                    {backlinkListQuery.error?.message || backlinkPackQuery.error?.message}
                  </div>
                ) : (
                  <>
                    <div className='space-y-1.5'>
                      <div className='text-sm font-semibold leading-6 text-foreground'>
                        {backlinkRequirement?.name || 'Requirement'}
                      </div>
                      <div className='break-words text-[15px] leading-7 text-foreground/90'>
                        {backlinkRequirement?.description || backlink?.requirementText || node.label}
                      </div>
                    </div>

                    <div className='space-y-3'>
                      <div className='flex items-center justify-between gap-3'>
                        <div className='text-sm text-muted-foreground'>
                          {backlink ? getBacklinkLocationLabel(backlink) : 'Backlink'}
                        </div>
                        {backlink?.confidence != null ? (
                          <Badge variant='outline' className='text-xs'>
                            {Math.round(backlink.confidence * 100)}%
                          </Badge>
                        ) : null}
                      </div>

                      {backlink?.matchedText ? (
                        <div className='break-words text-sm leading-6 text-foreground'>
                          "{backlink.matchedText}"
                        </div>
                      ) : null}

                      {backlinkReasoning ? (
                        <div className='break-words text-sm leading-6 text-muted-foreground italic'>
                          {backlinkReasoning}
                        </div>
                      ) : null}
                    </div>
                  </>
                )
              ) : (
                <div className='space-y-5'>
                  {detailRows.map((row) => (
                    <DetailSectionRow key={row.label} label={row.label} value={row.value} />
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {node.type === 'submission' ? (
            <div className='space-y-4'>
              {submissionId ? (
                submissionQuery.isLoading ? (
                  <div className='flex items-center gap-2 rounded-md border px-4 py-3 text-sm text-muted-foreground'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    Loading submission details…
                  </div>
                ) : submissionQuery.error ? (
                  <div className='flex items-center gap-2 rounded-md border border-red-200 px-4 py-3 text-sm text-status-red'>
                    <TriangleAlert className='h-4 w-4 text-destructive' />
                    {submissionQuery.error.message}
                  </div>
                ) : submissionQuery.data?.submission ? (
                  <SubmissionDetailsCard
                    submission={submissionQuery.data.submission}
                    isActiveSubmission={
                      submissionQuery.data.status?.activeSubmissionId === submissionQuery.data.submission.id
                    }
                    workspaceId={workspaceId}
                    testTitle={submissionQuery.data.status?.testTitle || submissionQuery.data.submission.topicTitle}
                    testCode={submissionQuery.data.status?.testCode}
                    testId={submissionQuery.data.status?.id || submissionQuery.data.submission.statusId}
                    compact
                    showViewButton={false}
                    embedded
                    hideAssessment
                    hideAssets
                    className='shadow-none'
                  />
                ) : null
              ) : null}

              {submissionEvidencePackId ? (
                submissionPackQuery.isLoading ? (
                  <div className='flex items-center gap-2 rounded-md border px-4 py-3 text-sm text-muted-foreground'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    Loading evidence files…
                  </div>
                ) : submissionPackQuery.error ? (
                  <div className='flex items-center gap-2 rounded-md border border-red-200 px-4 py-3 text-sm text-status-red'>
                    <TriangleAlert className='h-4 w-4 text-destructive' />
                    {submissionPackQuery.error.message}
                  </div>
                ) : submissionPackQuery.data ? (
                  <>
                    <ArtifactsPanel
                      artifacts={submissionPackQuery.data.artifacts}
                      selectedFile={selectedSubmissionFile}
                      onSelectFile={setSelectedSubmissionFile}
                    />

                    <Card className='shadow-none'>
                      <CardContent className='flex h-[520px] min-h-0 flex-col px-6 py-6'>
                        <MainPreviewArea
                          pack={submissionPackQuery.data}
                          selectedFile={selectedSubmissionFile}
                        />
                      </CardContent>
                    </Card>
                  </>
                ) : null
              ) : null}
            </div>
          ) : null}

          {node.type === 'pack' ? (
            <div className='space-y-4'>
              {submissionId ? (
                submissionQuery.isLoading ? (
                  <div className='flex items-center gap-2 rounded-md border px-4 py-3 text-sm text-muted-foreground'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    Loading submission assessment…
                  </div>
                ) : submissionQuery.error ? (
                  <div className='flex items-center gap-2 rounded-md border border-red-200 px-4 py-3 text-sm text-status-red'>
                    <TriangleAlert className='h-4 w-4 text-destructive' />
                    {submissionQuery.error.message}
                  </div>
                ) : submissionQuery.data?.submission ? (
                  <SubmissionDetailsCard
                    submission={submissionQuery.data.submission}
                    isActiveSubmission={
                      submissionQuery.data.status?.activeSubmissionId === submissionQuery.data.submission.id
                    }
                    workspaceId={workspaceId}
                    testTitle={submissionQuery.data.status?.testTitle || submissionQuery.data.submission.topicTitle}
                    testCode={submissionQuery.data.status?.testCode}
                    testId={submissionQuery.data.status?.id || submissionQuery.data.submission.statusId}
                    compact
                    showDetailedScores
                    showViewButton={false}
                    embedded
                    hideOverview
                    className='shadow-none'
                  />
                ) : null
              ) : null}
            </div>
          ) : null}
        </div>
      </ScrollArea>
    </Card>
  )
}
